import React, { useRef, useState, useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { Header } from '../Header'

import {
  FullPageLayout,
  Wrapper,
  Sidebar,
  Content,
  ContentContainer,
} from './SplitLayout.styles'

import { ResizeHandle } from '../ResizeHandle'

import {
  useOnClickOutside,
  DROPDOWN_SELECTOR,
  MODAL_SELECTOR,
  OPTION_ITEM,
  useResizableSidePanel,
} from '@sketch/utils'

import {
  callIfFunction,
  RenderFunctionOrNode,
} from '../SidebarLayout/callIfFunction'
import { useDefaultSidebarWidth } from '../SidebarLayout'

import { SearchExpandedProvider } from '../HeaderContext'
import { useTheme } from 'styled-components'
import { useLocalStorage } from 'react-use'
import { localStorageKeys } from '@sketch/constants'
import { SidebarWidthProvider } from './sidebarWidth.context'

const HIGHLIGHT_FEATURE_POPOVER =
  '[data-testid="new-feature-highlight-popover"]'

/**
 * TYPES
 */
interface SplitLayoutProps {
  title?: string
  /** Left sidebar, for navigation */
  sidebar?: RenderFunctionOrNode
  header: RenderFunctionOrNode
  footer: RenderFunctionOrNode
  children?: RenderFunctionOrNode
}

/**
 * COMPONENT
 * Used for documents view (homepage), it provides a left sidebar to show the
 * main navigation
 */
const SplitLayout: React.FCC<SplitLayoutProps> = ({
  title,
  sidebar,
  header,
  footer,
  children,
}) => {
  // Only used for mobile
  const [isSidebarLeftOpen, setSidebarLeftOpen] = useState(false)

  const theme = useTheme()

  const defaultWidth = useDefaultSidebarWidth()
  const [sidebarWidth, setSidebarWidth] = useLocalStorage(
    localStorageKeys.workspaceSidebarWidth,
    defaultWidth
  )

  const closeSidebar = () => {
    if (isSidebarLeftOpen) {
      // setTimeout is needed to make sure `setSidebarLeftOpen` is called after
      // any other click event listener, for example the hamburger icon click
      setTimeout(() => setSidebarLeftOpen(false), 0)
    }
  }

  // Close the left sidebar when the user clicks outside of it
  const sidebarRef = useRef<HTMLElement>(null)

  useOnClickOutside(sidebarRef, closeSidebar, {
    includeSelectors: [
      MODAL_SELECTOR,
      DROPDOWN_SELECTOR,
      OPTION_ITEM,
      HIGHLIGHT_FEATURE_POPOVER,
    ],
  })

  // Make sure the sidebar is closed every time the route changes
  const location = useLocation()
  useEffect(() => {
    closeSidebar()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location])

  const { width, resizeHandleRef } = useResizableSidePanel({
    defaultWidth: sidebarWidth ?? defaultWidth,
    minWidth: theme.sidebar.leftWidth,
    maxWidth: theme.sidebar.leftMaxWidth,
    onWidthUpdate: setSidebarWidth,
  })

  return (
    <SearchExpandedProvider>
      <FullPageLayout title={title}>
        <SidebarWidthProvider value={{ sidebarWidth: width }}>
          <Wrapper>
            {sidebar && (
              <Sidebar
                ref={sidebarRef}
                style={{ width }}
                isSidebarOpen={isSidebarLeftOpen}
              >
                {callIfFunction(sidebar)}
                <ResizeHandle ref={resizeHandleRef} />
              </Sidebar>
            )}

            <Content
              data-testid="page-content"
              isSidebarOpen={isSidebarLeftOpen}
            >
              <Header>
                {callIfFunction(header, {
                  isSidebarLeftOpen,
                  setSidebarLeftOpen,
                })}
              </Header>

              <ContentContainer isSidebarOpen={isSidebarLeftOpen}>
                {callIfFunction(children)}
              </ContentContainer>
              {callIfFunction(footer)}
            </Content>
          </Wrapper>
        </SidebarWidthProvider>
      </FullPageLayout>
    </SearchExpandedProvider>
  )
}

export { SplitLayout }
